import { Box, Button, Stack } from '@mui/material'
import PageSection from '../../../Constants&Theme/PageSection'
import { useNavigate, useParams } from 'react-router-dom'
import { tsLimsOrder } from '../../../Typescript/API_Response'
import { getCodeSetLabelFromList, getCodeSetLabelLookup, getLabelFromList } from '../../../Functions/CodeSetFns'
import { useBioBankContext } from '../../../Contexts/Modules/BioBankModule'
import { ReadOnlyTextField } from '../../../garvan-react/Components/ReadOnlyTextField'
import { useUserContext } from '../../../Contexts/UserConfig'
import { stackGap } from '../../../Constants&Theme/Spacing'
import { formatValueDate, formatValueDateTime } from '../../../Functions/FormatValues'
import { Cancel, Update } from '@mui/icons-material'
import { useState } from 'react'
import ChangeDeliveryDialog from './ChangeDeliveryDialog'
import { usePyGet } from '../../../APIs/Hooks/PyAPIHooks'
import ConfirmCancelDialog from './ConfirmCancelDialog'
import { DataGridPro, GridRowSelectionModel } from '@mui/x-data-grid-pro'
import { useGetSampleIssueCols } from '../SharedItems/SampleSaleCols'
import ConfirmCancelItemsDialog from './ConfirmCancelItemsDialog'

function OrderDetails() {
  const { loginConfig } = useUserContext()
  const { order_id } = useParams()
  const { ownersQuery, statusQuery } = useBioBankContext()
  const navigate = useNavigate()
  const cols = useGetSampleIssueCols()
  const orderQuery = usePyGet(['/lims/issue', `/${order_id}`], !!order_id)
  const order: tsLimsOrder = orderQuery.data
  const [selection, setSelection] = useState<GridRowSelectionModel>([])
  const [open, setOpen] = useState<undefined | string>(undefined)

  if (!order_id) {
    navigate('./', { replace: true })
  }
  return (
    <Box sx={{ maxWidth: 'lg', width: 1 }}>
      <PageSection title={'Order: ' + order_id}>
        <Stack
          gap={stackGap.col}
          width={1}
        >
          <Stack
            direction={'row'}
            gap={stackGap.row}
            width={1}
          >
            <ReadOnlyTextField
              fullWidth
              label={'LIMS Order ID'}
              value={order?.LIMSOrderId ?? ''}
            />
            <ReadOnlyTextField
              fullWidth
              label={'Shipment Date'}
              value={formatValueDate(order?.IssueDate) ?? ''}
            />
            <ReadOnlyTextField
              fullWidth
              multiline
              label={'Van Run'}
              value={order?.VanRunName ?? ''}
            />
            <ReadOnlyTextField
              fullWidth
              label={'Issue Status'}
              value={getCodeSetLabelFromList(
                statusQuery.data,
                order?.IssueStatus,
              )}
            />
          </Stack>
          <Stack
            direction={'row'}
            gap={stackGap.row}
            width={1}
          >
            <ReadOnlyTextField
              sx={{ flex: 1.5 }}
              label={'To Facility'}
              value={
                getCodeSetLabelLookup(
                  loginConfig,
                  ['KV-Facilities'],
                  order?.ToFacility,
                ) || ''
              }
            />
            <ReadOnlyTextField
              sx={{ flex: 1.5 }}
              label={'To Owner'}
              value={getLabelFromList(
                ownersQuery.data ?? [],
                order?.ToOwner,
                'OwnerID',
                'OwnerName',
              )}
            />
            <ReadOnlyTextField
              sx={{ flex: 1.5 }}
              label={'Order Contact'}
              value={order?.OrderContact ?? ''}
            />
            <ReadOnlyTextField
              sx={{ flex: 1 }}
              label={'PO Number'}
              value={order?.PONumber ?? ''}
            />
            <ReadOnlyTextField
              sx={{ flex: 1 }}
              label={'Project ID'}
              value={order?.WorkdayProjectID ?? ''}
            />
          </Stack>
          <Stack
            direction={'row'}
            gap={stackGap.row}
            width={1}
          >
            <ReadOnlyTextField
              fullWidth
              multiline
              label={'Delivery Address'}
              value={order?.DeliveryAddress ?? ''}
            />
            <ReadOnlyTextField
              fullWidth
              label={'Delivery Instructions'}
              value={order?.DeliveryInstructions ?? ''}
            />
          </Stack>
          <Stack
            direction={'row'}
            gap={stackGap.row}
            width={1}
          >
            <Button
              disabled={!order?._editable || selection.length < 1}
              variant={'outlined'}
              color={'error'}
              startIcon={<Cancel />}
              onClick={() => setOpen('cancel_items')}
            >
              Cancel Selection
            </Button>
            <Button
              sx={{ ml: 'auto' }}
              disabled={!order?._editable}
              variant={'outlined'}
              color={'error'}
              startIcon={<Cancel />}
              onClick={() => setOpen('cancel')}
            >
              Cancel Order
            </Button>
            <Button
              disabled={!order?._editable}
              variant={'outlined'}
              startIcon={<Update />}
              onClick={() => setOpen('update')}
            >
              Update Shipment Details
            </Button>
          </Stack>
          <PageSection
            title={'Requested Samples'}
            headingLevel={'h2'}
          >
            <DataGridPro
              getRowId={(row) => row?.LIMSSampleId}
              sx={{ width: 1 }}
              autoHeight
              rowSelectionModel={selection}
              onRowSelectionModelChange={setSelection}
              loading={orderQuery.isLoading}
              checkboxSelection={order?._editable}
              disableRowSelectionOnClick={!order?._editable}
              columns={cols}
              rows={order?.samples ?? []}
            />
          </PageSection>
          <PageSection
            title={'History Log'}
            headingLevel={'h2'}
          >
            <DataGridPro
              sx={{ width: 1 }}
              rows={order?.logs ?? []}
              columns={[
                {
                  field: 'LIMSOrderLogId',
                  headerName: 'ID',
                  flex: 0.5,
                },
                {
                  field: 'LogDate',
                  headerName: 'Date',
                  flex: 1,
                  valueFormatter: (value: any) => formatValueDateTime(value),
                },
                {
                  field: 'LogMessage',
                  headerName: 'Message',
                  flex: 5,
                },
              ]}
              getRowId={(row) => row.LIMSOrderLogId}
              disableColumnMenu
              autoHeight
              hideFooter
              density={'compact'}
              loading={orderQuery.isLoading}
              initialState={{
                sorting: {
                  sortModel: [{ field: 'LogDate', sort: 'asc' }],
                },
              }}
            />
          </PageSection>
        </Stack>
      </PageSection>
      <ChangeDeliveryDialog
        open={open}
        setOpen={setOpen}
        orderQuery={orderQuery}
      />
      <ConfirmCancelDialog
        open={open}
        setOpen={setOpen}
      />
      <ConfirmCancelItemsDialog
        open={open}
        setOpen={setOpen}
        items={selection as number[]}
      />
    </Box>
  )
}

export default OrderDetails
